import React from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {Outlet, useLocation, useNavigate} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {useExactSelector} from '@edna/hooks';

import Layout from 'src/containers/Layout';
import {selectTenantId, useGetCurrentUserQuery} from 'src/containers/User';
import {routeChanged} from 'src/models';
import PATHS from 'src/paths';
import {sendPageMetrics} from 'src/utils';
import {setTitle} from 'src/utils/pageTitle';

import {selectIsServiceAvailable, setServiceAvailability} from './slice';
import {useMetrics} from './useMetrics';

export const App = React.memo(() => {
  const {i18n} = useTranslation();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tenantId = useExactSelector(selectTenantId);
  const isServiceAvailable = useExactSelector(selectIsServiceAvailable);
  const {isLoading: isUserLoading} = useGetCurrentUserQuery(!tenantId ? skipToken : undefined);

  useMetrics();

  React.useLayoutEffect(() => {
    if (pathname !== PATHS.UNAVAILABLE) {
      dispatch(setServiceAvailability(true));
    }

    dispatch(routeChanged(pathname));
    sendPageMetrics(pathname);
  }, [pathname]);

  React.useEffect(() => {
    setTitle(i18n, pathname, isUserLoading);
  }, [pathname, isUserLoading, i18n]);

  React.useEffect(() => {
    if (!isServiceAvailable) {
      navigate(PATHS.UNAVAILABLE);
    }
  }, [isServiceAvailable]);

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
});

App.displayName = 'App';
